import { useCallback, useEffect, useState } from "react";
import { Card, Col, Container, Row } from "react-bootstrap"
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

import ApiService from "../services/ApiService";
import CustomTooltip from "../components/common/CustomTooltip";
import { getGameAssetsUrl, getProfilePicture } from "../utilities/Helpers";

import global from '../styles/images/global-colored.png';
import controller from '../styles/images/controller.png';
import coin from '../styles/images/coin.png';

const LeaderboardsPage = () => {
    const [gameId, setGameId] = useState<number>(0); // 0 = all games, > 0 = specific game

    function handleGameChange(event: any) {
        let value = Number(event.currentTarget.value);
        setGameId(value);
    }

    const LeaderboardData = ({ wagered, game }: { wagered: number, game: number }) => {
        const [timeframe, setTimeframe] = useState<number>(0);
        const [leaderboardData, setLeaderboardData] = useState<any[]>();

        const updateLeaderboard = useCallback((game: number = 0, timeframe: number = 0) => {
            ApiService.get(`/leaderboard/?gameId=${game}&wagered=${wagered}&timeframe=${timeframe}`).then((response) => {
                if (!response) throw new Error("No response");
                if (!response.success) throw new Error(response.message);
                setLeaderboardData(response.data);
            }).catch((error) => console.log(error));
        }, [wagered]);

        useEffect(() => {
            updateLeaderboard(game, timeframe);
        }, [timeframe, updateLeaderboard, game]);

        function handleTimeframeChange(event: any) {
            let value = Number(event.currentTarget.value);
            setTimeframe(value);
            updateLeaderboard(game, value);
        }

        if (!leaderboardData) return <tr><td colSpan={7}>Loading...</td></tr>;
        if (leaderboardData.length === 0) return <tr><td colSpan={7}>No records found</td></tr>;

        return (<>{leaderboardData.map((data, index) => {
            let total = Number(data.wins) + Number(data.losses);
            let ratio = (data.wins / total).toFixed(2);
            let pfp = getProfilePicture(data.address);

            const GameTooltip = () => {
                return (
                    <CustomTooltip title="Dark Pong" id="dark-pong-casual" placement={"bottom"}>
                        <Link to={`/game/${data.game}`}>
                            <img src={`${getGameAssetsUrl(1)}/icon.png`} alt="Dark Pong" height={32} className="d-none d-md-inline-block" />
                            <img src={`${getGameAssetsUrl(1)}/icon.png`} alt="Dark Pong" height={24} className="d-inline-block d-md-none" />
                        </Link>
                    </CustomTooltip>
                );
            }

            return (
                <tr key={data.id}>
                    <td>
                        # {index + 1}
                    </td>
                    <td className="text-start">
                        <Link to={`/profile/${data.address}`}>
                            <img src={pfp} alt={data.address} height={32} className="d-none d-md-inline-block" /> {data.name}
                        </Link>
                    </td>
                    {gameId === 0 && (
                        <td>
                            <GameTooltip />
                        </td>
                    )}
                    <td>
                        {data.wins}
                    </td>
                    <td>
                        {data.losses}
                    </td>
                    <td>
                        {ratio}
                    </td>
                </tr>
            );
        })}</>);
    }

    return (
        <>
            <Helmet>
                <title>Darknet Games | Leaderboards</title>
            </Helmet>
            <Container>
                <Row className="justify-content-center">
                    <Col>
                        <Card className="card-widget" id="leaderboard-widget">
                            <Card.Header>
                                <Card.Title className="text-light text-center fs-3 fs-3">
                                    Leaderboards
                                </Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <Row className="justify-content-start g-1 g-md-3">
                                    <Col xs={3} md={1}>
                                        <CustomTooltip title="Global" id="global-icon" placement={"bottom"}>
                                            <button className={`dg-btn dg-btn-block ${(gameId === 0) ? "dg-btn-primary text-light" : "dg-btn-secondary"}`} value={0} onClick={handleGameChange}>
                                                <img src={global} alt="Global" height={25} />
                                            </button>
                                        </CustomTooltip>
                                    </Col>
                                    <Col xs={3} md={1}>
                                        <CustomTooltip title="Dark Pong" id="dark-pong-icon" placement={"bottom"}>
                                            <button className={`dg-btn dg-btn-block ${(gameId === 1) ? "dg-btn-primary text-light" : "dg-btn-secondary"}`} value={1} onClick={handleGameChange}>
                                                <img src={`${getGameAssetsUrl(1)}/icon.png`} alt="Dark Pong" height={25} />
                                            </button>
                                        </CustomTooltip>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                        <Row className="justify-content-center mt-4">
                            <Col>
                                <Card className="card-widget" id="wagered-widget">
                                    <Card.Header>
                                        <h5>
                                            <img src={coin} alt="Wagered" height={25} /> Wagered Games
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="px-0 px-md-3 pb-0 pb-md-3">
                                        <table className="table text-light text-center">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Rank</th>
                                                    <th scope="col">Player</th>
                                                    {gameId === 0 && (
                                                        <th scope="col">Game</th>
                                                    )}
                                                    <th scope="col">W<span className="d-none d-md-inline-block">ins</span></th>
                                                    <th scope="col">L<span className="d-none d-md-inline-block">osses</span></th>
                                                    <th scope="col">
                                                        <span className="d-none d-md-inline-block">Win&nbsp;</span>
                                                        Ratio
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <LeaderboardData wagered={1} game={gameId} />
                                            </tbody>
                                        </table>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col>
                                <Card className="card-widget mt-4 mt-md-0" id="wagered-widget">
                                    <Card.Header>
                                        <h5>
                                            <img src={controller} alt="Casual" height={25} /> Casual Games
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="px-0 px-md-3 pb-0 pb-md-3">
                                        <table className="table text-light text-center">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Rank</th>
                                                    <th scope="col">Player</th>
                                                    {gameId === 0 && (
                                                        <th scope="col">Game</th>
                                                    )}
                                                    <th scope="col">W<span className="d-none d-md-inline-block">ins</span></th>
                                                    <th scope="col">L<span className="d-none d-md-inline-block">osses</span></th>
                                                    <th scope="col">
                                                        <span className="d-none d-md-inline-block">Win&nbsp;</span>
                                                        Ratio
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <LeaderboardData wagered={0} game={gameId} />
                                            </tbody>
                                        </table>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Col>
                </Row >
            </Container >
        </>
    );
}

export default LeaderboardsPage;